import { CSSProperties } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { Alert, AlertTitle, Paper, Stack } from '@mui/material'

import type { BulkLoads as BulkLoadsType } from '../../../types'

import { FieldHeader } from './components/FieldHeader'
import { PartialList } from './components/PartialList'

import { useActions } from './hooks/useActions'
import { useAddLoadsOptions } from './hooks/useAddLoadsOptions'
import { useComputeAddablePartialLoads } from './hooks/useComputeAddablePartialLoads'
import { useReachedMaxLoads } from './hooks/useReachedMaxLoads'
import { useSplitAndSortLoadsByType } from './hooks/useSplitAndSortLoadsByType'
import { useTexts } from './hooks/useTexts'
import { Field } from './components/Field'

import { INITIAL_LOAD_FIELD_KEY } from './constants'

const MainPaperStyle: CSSProperties = {
  background: '#F3F5F7',
  padding: '16px 8px',
}

type RequiredFormField = BulkLoadsType

type Props<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = RequiredFormField,
> = {
  label: string
  addLoadLabel: string
  helperText?: string
  testId?: string
  disabled?: boolean
  maxLoadsForOrder: number
} & TidalFormBridgeOptions<RequiredFormField, TargetField, State, AcceptedFormValue>

export function BulkLoads<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = RequiredFormField,
>(props: Props<TargetField, State, AcceptedFormValue>) {
  const {
    helperText,
    label,
    addLoadLabel,
    maxLoadsForOrder,
    testId,
    disabled,
    initialValue,
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      field: { visible },
      value: fieldValue,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<RequiredFormField, TargetField, State, AcceptedFormValue>({
    validateOn,
    initialValue,
    ...bridgeOptions,
  })

  const texts = useTexts()
  const { exact, partial } = useSplitAndSortLoadsByType(fieldValue)

  const loadsOptions = useAddLoadsOptions(fieldValue)
  const reachedMaxLoads = useReachedMaxLoads(fieldValue, maxLoadsForOrder)
  const addablePartialLoads = useComputeAddablePartialLoads(
    partial,
    fieldValue.loadsDistribution,
    maxLoadsForOrder,
  )

  const actions = useActions(fieldValue, onChange)
  const disableTooltipReason = reachedMaxLoads ? 'limitReached' : undefined

  const hasNewLoad = Object.keys(fieldValue.exact).some(
    key => key !== INITIAL_LOAD_FIELD_KEY && !loadsOptions.some(opt => opt.label === key),
  )

  if (!visible) return null

  return (
    <Stack spacing={2} data-testid={testId ?? 'bulk-loads_root'}>
      <FieldHeader
        fieldLabel={label}
        testId={`${testId}-section-header`}
        action={{
          onClick: actions.onAddLoad,
          disabled: reachedMaxLoads || disabled || fieldDisabled,
          label: texts.addLoad,
          tooltip:
            fieldDisabled || disabled
              ? texts.fieldDisabled
              : texts.addLoadTooltip(disableTooltipReason, maxLoadsForOrder),
        }}
      />
      {exact.length > 0 || partial.length > 0 ? (
        <Paper elevation={0} sx={MainPaperStyle}>
          <Stack spacing={3}>
            <Field
              onUpdateSelect={actions.onUpdateSelect}
              onRemove={name => actions.onDeleteLoad(name, 'exact')}
              value={exact}
              disabled={disabled}
              onUpdateValue={actions.onUpdateValue}
              options={loadsOptions}
              error={showErrorAccent && fieldHasError}
              errorText={errorOrHelperText}
              ordersCount={fieldValue.affectedOrdersCount}
            />

            {hasNewLoad && (
              <Alert severity="warning">
                <AlertTitle>{texts.newLoadWarningHeader}</AlertTitle>
                {texts.newLoadWarningContent}
              </Alert>
            )}

            <PartialList
              loads={partial}
              onDeleteLoad={name => actions.onDeleteLoad(name, 'partial')}
              onAddLoad={actions.onAddToAll}
              ordersCount={fieldValue.affectedOrdersCount}
              addableLoads={addablePartialLoads}
              maxLoadsForOrder={maxLoadsForOrder}
              disabled={fieldDisabled}
            />
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  )
}
